import { createSlice, isAnyOf, type PayloadAction } from "@reduxjs/toolkit";
import type {
  FileDetailsConstituent,
  FileDetailsRecord,
  FileDetailsVersion,
  FileDependencyItem,
} from "@ServerTypes/fileOperations/fileOperations.schemas";
import type { ApiPostResponse } from "@Services/axiosShared";
import type { UpdateFileUnitsRequest } from "@ServerTypes/queries/queries.schema";

/** `/fileOperations/getFileDetails` records, keyed by the id of the version each was opened by. */
type FilesDataState = Record<string, FileDetailsRecord>;
type FileDependenciesState = Record<string, FileDependencyItem[]>;
/** `/getResourcesUnderWhichFilesShared`'s body verbatim: the transmittal rows, with no status envelope. */
type SharedResourcesResponse = ApiPostResponse<"/fileOperations/getResourcesUnderWhichFilesShared">;
type FilesSharedUnderResourcesState = Record<string, SharedResourcesResponse>;

const filesDataSlice = createSlice({
  name: "filesData",
  initialState: {} as FilesDataState,
  reducers: {
    setFilesData: (state, action: PayloadAction<Record<string, FileDetailsRecord>>) => ({
      ...state,
      ...action.payload,
    }),
    resetFilesData: (state) => state,
    modifyFileUnitsData: (
      state,
      action: PayloadAction<{ fileVersionId: string; unitsNew: UpdateFileUnitsRequest["unitsNew"] }>,
    ) => {
      const { fileVersionId, unitsNew } = action.payload;
      const fileId = state[fileVersionId]?._id;
      if (fileId === undefined) return state;
      // `Units` is the file's, and a file is held once for every version it was opened by.
      return Object.fromEntries(
        Object.entries(state).map(([key, file]) => [
          key,
          file._id === fileId ? { ...file, Units: { ...file.Units, ...unitsNew } } : file,
        ]),
      );
    },
  },
});

const fileDependenciesSlice = createSlice({
  name: "fileDependencies",
  initialState: {} as FileDependenciesState,
  reducers: {
    setFileDependencies: (state, action: PayloadAction<{ fileVersionId: string; data: FileDependencyItem[] }>) => {
      const { fileVersionId, data } = action.payload;
      state[fileVersionId] = [...data];
    },
  },
});

const filesSharedUnderResourcesSlice = createSlice({
  name: "filesSharedUnderResources",
  initialState: {} as FilesSharedUnderResourcesState,
  reducers: {
    setResourcesUnderWhichFilesShared: (
      _state,
      action: PayloadAction<{ fileId: string; data: SharedResourcesResponse }>,
    ) => ({
      [action.payload.fileId]: action.payload.data,
    }),
  },
});

export interface DependencyQuerySettings {
  searchParameter: string;
  searchCondition: string;
  multiDependencyPreference: string;
}

type DependencyQuerySettingsState = Record<string, DependencyQuerySettings | null>;

const dependencyQuerySettingsSlice = createSlice({
  name: "dependencyQuerySettings",
  initialState: {} as DependencyQuerySettingsState,
  reducers: {
    setDependencyQuerySettings: (
      state,
      action: PayloadAction<{ fileVersionId: string; data: DependencyQuerySettings | null }>,
    ) => {
      state[action.payload.fileVersionId] = action.payload.data;
    },
  },
});

const currentFileVersionIdSlice = createSlice({
  name: "currentFileVersionId",
  initialState: "",
  reducers: {
    setFileVersionId: (_state, action: PayloadAction<string>) => action.payload,
  },
});

/** The versions whose record the server refused (getFileDetails), so the viewer stops waiting for them. */
const fileRecordRefusalsSlice = createSlice({
  name: "fileRecordRefusals",
  initialState: {} as Record<string, true>,
  reducers: {
    markFileRecordRefused: (state, action: PayloadAction<string>) => {
      state[action.payload] = true;
    },
    markFileRecordRead: (state, action: PayloadAction<string>) => {
      // The same state when it was not refused, so the conversion poll's reads cause no render.
      if (!Object.hasOwn(state, action.payload)) return state;
      const { [action.payload]: _read, ...refused } = state;
      return refused;
    },
  },
});

/**
 * The versions whose record read still got no answer after its last try (getFileDetails), for this open: the viewer
 * stops waiting for them and maps the scene without them. Opening a version (setFileVersionId) starts with none, so the
 * next open reads and waits for each again; an answer about one, its record or the server's refusal, withdraws it.
 */
const fileRecordsUnavailableSlice = createSlice({
  name: "fileRecordsUnavailable",
  initialState: {} as Record<string, true>,
  reducers: {
    markFileRecordUnavailable: (state, action: PayloadAction<string>) => {
      state[action.payload] = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(currentFileVersionIdSlice.actions.setFileVersionId, (state) => {
        for (const versionId of Object.keys(state)) delete state[versionId];
      })
      // Deleting a version that is not marked changes nothing, so the conversion poll's reads cause no render.
      .addMatcher(
        isAnyOf(
          fileRecordRefusalsSlice.actions.markFileRecordRead,
          fileRecordRefusalsSlice.actions.markFileRecordRefused,
        ),
        (state, action) => {
          delete state[action.payload];
        },
      );
  },
});

/**
 * Read one entry out of the `filesData` slice by an id that may be absent.
 *
 * The viewer's `fileVersionId` is `undefined` while no file is mounted, and `Record<string, T>`
 * cannot be indexed by `string | undefined`. A missing id simply has no entry, which is what
 * every caller already handles.
 */
export const selectFileVersionData = (
  filesData: FilesDataState,
  fileVersionId: string | undefined,
): FileDetailsRecord | undefined => (fileVersionId === undefined ? undefined : filesData[fileVersionId]);

/** The version `fileVersionId` names, out of its file's record. */
export const versionOf = (
  file: FileDetailsRecord | undefined,
  fileVersionId: string | undefined,
): FileDetailsVersion | undefined =>
  fileVersionId === undefined ? undefined : file?.versions?.find((version) => version._id === fileVersionId);

/**
 * Constituent version ids, as a merged-model version names them in `constituentFileIds` (the models it loads) or
 * `constituentFiles` (the sources it was merged from), each resolved through the record's `constituentFiles` to
 * that version and the file holding it, in the given order. An id the record carries no version for is left out.
 */
export const resolveConstituents = (
  file: Pick<FileDetailsRecord, "constituentFiles"> | undefined,
  constituentVersionIds: readonly string[] | null | undefined,
): { file: FileDetailsConstituent; version: FileDetailsVersion }[] =>
  (constituentVersionIds ?? []).flatMap((constituentVersionId) => {
    for (const constituent of file?.constituentFiles ?? []) {
      const version = constituent.versions.find((entry) => entry._id === constituentVersionId);
      if (version) return [{ file: constituent, version }];
    }
    return [];
  });

/** What the merged-model version `fileVersionId` loads: its `constituentFileIds`, resolved. */
export const constituentsOf = (
  file: FileDetailsRecord | undefined,
  fileVersionId: string | undefined,
): { file: FileDetailsConstituent; version: FileDetailsVersion }[] =>
  resolveConstituents(file, versionOf(file, fileVersionId)?.constituentFileIds);

export const { setFilesData: setFilesDataAction, resetFilesData, modifyFileUnitsData } = filesDataSlice.actions;

export const { setFileDependencies: setFileDependenciesAction } = fileDependenciesSlice.actions;

export const { setDependencyQuerySettings: setDependencyQuerySettingsAction } = dependencyQuerySettingsSlice.actions;

export const { setResourcesUnderWhichFilesShared: setResourcesUnderWhichFilesSharedAction } =
  filesSharedUnderResourcesSlice.actions;

export const { setFileVersionId: setFileVersionIdAction } = currentFileVersionIdSlice.actions;

export const { markFileRecordRefused, markFileRecordRead } = fileRecordRefusalsSlice.actions;

export const { markFileRecordUnavailable } = fileRecordsUnavailableSlice.actions;

export const setFilesData = filesDataSlice.reducer;
export const setFileDependencies = fileDependenciesSlice.reducer;
export const dependencyQuerySettings = dependencyQuerySettingsSlice.reducer;
export const filesSharedUnderResources = filesSharedUnderResourcesSlice.reducer;
export const currentFileVersionId = currentFileVersionIdSlice.reducer;
export const fileRecordRefusals = fileRecordRefusalsSlice.reducer;
export const fileRecordsUnavailable = fileRecordsUnavailableSlice.reducer;
